/** @format */

import { CollectCurrency } from "../../../utility/types/collectTypes";

// ─────────────────────────────────────────────────────────────────────────
// Collect plan pricing (client-side display only).
//
// Decision D2: flat nominal pricing per local currency — no FX conversion.
// The authoritative charge amounts live in Stripe (server-selected by
// currency); these constants are ONLY for rendering the pricing/checkout and
// billing screens. Keep the numbers in sync with the Stripe prices.
//   Intro:    99 / month × 2 cycles
//   Standard: 149 / month ongoing
// ─────────────────────────────────────────────────────────────────────────

export const COLLECT_INTRO_AMOUNT = 99;
export const COLLECT_STANDARD_AMOUNT = 149;
export const COLLECT_INTRO_CYCLES = 2;

interface CurrencyDisplay {
    symbol: string;
    /** ISO code used with Intl.NumberFormat. */
    code: string;
    /** BCP-47 locale for currency formatting. */
    locale: string;
}

const CURRENCY_DISPLAY: Record<CollectCurrency, CurrencyDisplay> = {
    usd: { symbol: "$", code: "USD", locale: "en-US" },
    cad: { symbol: "C$", code: "CAD", locale: "en-CA" },
    gbp: { symbol: "£", code: "GBP", locale: "en-GB" },
};

const DEFAULT_CURRENCY: CollectCurrency = "usd";

export function normalizeCurrency(
    value?: string | null,
): CollectCurrency {
    const c = (value || "").toLowerCase();
    if (c === "usd" || c === "cad" || c === "gbp") return c;
    return DEFAULT_CURRENCY;
}

/** Format a whole-unit amount in the given currency, e.g. 99 → "$99.00". */
export function formatCollectMoney(
    amount: number,
    currency?: string | null,
): string {
    const cur = normalizeCurrency(currency);
    const display = CURRENCY_DISPLAY[cur];
    try {
        return new Intl.NumberFormat(display.locale, {
            style: "currency",
            currency: display.code,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount);
    } catch {
        return `${display.symbol}${amount.toFixed(2)}`;
    }
}

export function collectIntroPrice(currency?: string | null): string {
    return formatCollectMoney(COLLECT_INTRO_AMOUNT, currency);
}

export function collectStandardPrice(currency?: string | null): string {
    return formatCollectMoney(COLLECT_STANDARD_AMOUNT, currency);
}

/** Short human date, e.g. "Jul 3, 2026". Empty string for missing input. */
export function formatCollectDate(value?: string | null): string {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
}

/** Whole days from now until `value` (never negative). 0 when past/missing. */
export function daysUntil(value?: string | null): number {
    if (!value) return 0;
    const target = new Date(value).getTime();
    if (Number.isNaN(target)) return 0;
    const ms = target - Date.now();
    if (ms <= 0) return 0;
    return Math.ceil(ms / (24 * 60 * 60 * 1000));
}
